// ---------- l'écran de lancement ----------
//
// Le calque qui couvre le démarrage, du tout premier pixel jusqu'à ce que le catalogue soit
// peint. Deux raisons d'exister, et la première est un défaut à corriger :
//
// 1. AVANT lui, le lancement laissait voir ce qu'il y avait dessous. Android affiche sa
//    fenêtre de démarrage — souvent l'instantané de la dernière image de l'app, c'est-à-dire
//    la feuille que le joueur venait de quitter — puis la WebView prenait la main. Un calque
//    opaque posé dans le balisage, donc présent dès la première image peinte, ferme ce trou
//    par construction : il n'y a plus d'instant où quoi que ce soit d'autre soit visible.
//
// 2. Ensuite seulement, il porte une marque : le renard, le nom, l'accroche. Le renard et le mot
//    sont ceux de `.home-title` (public/icons/renard.webp et le mot en Fredoka sous son dégradé)
//    — quand le calque se retire, le joueur retrouve en haut du catalogue, en petit, ce qu'il
//    venait de voir en grand.
//
// LE TAP (2026-10-30). Jusqu'ici la séquence posait la diagonale de l'ancienne icône case par
// case. Avec le renard, six effets ont été maquettés (planche
// https://claude.ai/artifact/MXAnR4u8oc3tvyhY7WQPn3) et celui-ci a été retenu parce qu'il montre
// LE geste du jeu : une silhouette de cases blanches, un doigt touche le museau, et la couleur se
// répand d'un coup à partir de là — c'est la règle `flood`, un tap remplit toute la zone. Une
// première version mettait un petit code au graphite dans chaque case blanche, comme sur la
// feuille ; à cette taille et sans légende personne ne les comprenait, elles sont vides.
//
// Les couleurs sont les jetons de la charte, les deux polices sont déjà dans le bundle — Fredoka
// pour le nom, Nunito (variable 400..800) pour l'accroche.

import { EASE, noMotion } from './anim';
import { t } from './i18n';

/** Le renard détouré, produit par scripts/gen-icons.py — le même fichier que l'en-tête du
 *  catalogue, donc déjà en cours de chargement quand ce module s'exécute : l'<img> de l'en-tête
 *  est dans le balisage. Chemin relatif, servi par Vite comme par l'APK (public/ est copié à
 *  la racine). */
const FOX_SRC = 'icons/renard.webp';

/**
 * Le découpage du renard en cases, par côté. L'image est générée, et ses tuiles ne suivent PAS
 * une grille régulière : mesuré le 2026-10-30, les briques ont un pas de ~92 px sur 1254 en
 * largeur et de ~88 en hauteur, et les zones (peinture, triangles, point de croix) sont décalées
 * entre elles d'une fraction de case. Aucun découpage ne tombe juste partout ; 14 est le plus
 * proche du pas moyen. Ça ne se voit pas : chaque case n'est découpée que pendant la vague, une
 * demi-seconde, et l'image finale est redessinée d'un seul tenant (cf. draw).
 */
const G = 14;
/** Le museau, où le doigt touche — en fraction de l'image, relevé sur renard.webp. */
const TAP_X = 0.5;
const TAP_Y = 0.68;
/** Une case n'entre dans la silhouette blanche que si le renard la couvre à ce point. En
 *  dessous, ce sont des bouts de fourrure au bord : en papier, ils feraient un escalier de
 *  carrés blancs autour d'une silhouette qui est ronde. Ils apparaissent quand même avec la
 *  vague, puisque l'image finale est entière. */
const PAPER_COVER = 0.4;
/** Le temps qu'on laisse à l'image pour arriver avant de jouer la séquence sans elle. Elle
 *  est locale dans l'APK et sort du cache du service worker sur le web : en pratique elle est là
 *  dès la première image. Au-delà, le nom et l'accroche seuls valent mieux qu'un sol vide. */
const IMG_WAIT_MS = 500;

/** Les jetons de la charte. LUS dans la feuille de style, alors qu'ils étaient écrits en dur
 *  ici : depuis qu'il y a trois thèmes, un calque de lancement mûre s'ouvrirait sur un
 *  catalogue clair, et l'écran de lancement raterait très exactement ce qu'il existe pour
 *  faire — ne pas laisser voir de couture au démarrage.
 *
 *  La lecture est sûre à cet endroit, contrairement à ce que disait la note d'avant : main.ts
 *  importe `./css/style.css` en PREMIER (ligne 3), donc la feuille est appliquée avant que ce
 *  module ne s'exécute — en production elle est même un <link> dans <head>. Le thème, lui, est
 *  posé sur <html> par le script en tête de index.html, avant la première image.
 *
 *  Les valeurs de repli sont celles du thème par défaut, comme dans palette.ts. */
const tok = (name: string, fallback: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
const GROUND = tok('--bg', '#ded3f7');
/** Les cases encore à peindre : le papier de la feuille, qui ne change pas avec le thème. */
const PAPER = tok('--paper', '#fbf6ec');
/** Les deux tons du nom, qui se pose sur le SOL : le vif en haut, sa version lisible en
 *  bas — le même dégradé que .wordmark dans style.css, aux mêmes jetons. */
const ACC2 = tok('--acc2', '#957cff');
const ACC_TXT = tok('--acc-txt', '#5b3fc4');
/** L'encre de l'interface, celle qui est faite pour se lire SUR le sol du thème. C'est
 *  l'accroche qui la porte — voir line() pour pourquoi ce n'était pas le cas. */
const INK_UI = tok('--ink', '#2c2350');
const RULE = 'rgba(60, 52, 92, 0.16)';

const NAME = t('brand.name');
/** La coupe tombe sur le sens et non sur la mesure : « ce qu'elle cache » est la chute, elle
 *  reste entière. Changer la phrase demande de recouper à la main.
 *
 *  `fit()` plus bas calcule la taille de police à partir de la LONGUEUR EXACTE de ces deux
 *  lignes (34 caractères au total) — une traduction nettement plus longue déborderait la mise
 *  en page tant que `fit()` n'est pas revu. Hors périmètre tant qu'on reste en français. */
const L1 = t('splash.line1');
const L2 = t('splash.line2');

/**
 * Durée minimale d'affichage, et c'est une DÉCISION, pas une contrainte technique.
 *
 * Sans elle, le calque se retire à l'instant où le catalogue est prêt : sur un rechargement
 * tiède ça fait 200 ms, et la séquence n'est jamais vue — on aurait payé une animation que
 * personne ne regarde. Avec elle, le lancement dure au moins ce temps-là même quand tout est
 * prêt avant. Elle se compte depuis le DÉBUT DE LA SÉQUENCE, pas depuis l'appel : si l'image du
 * renard tarde, la séquence part plus tard et ne doit pas être coupée pour autant.
 *
 * 1950 ms est calé sur la séquence ci-dessous, qui se termine à 1460 ms : les 490 ms qui restent
 * sont le temps pendant lequel l'accroche est LUE, une fois qu'elle a fini de bouger. C'était
 * 1600 avec la diagonale, dont la séquence finissait à 1080 : le tap a une étape de plus (la
 * silhouette blanche, PUIS le doigt, PUIS la couleur), et c'est elle qui fait comprendre le jeu.
 *
 * C'EST LE BOUTON DE DURÉE, et le seul. L'allonger tient l'écran plus longtemps sans rien
 * changer au rythme — mais au-delà d'un demi-seconde de plus, la tenue devient une pause
 * immobile, et une pause se lit comme un blocage et non comme une marque. Pour un lancement
 * plus lent, ce sont les constantes du rythme juste en dessous qu'il faut étirer, pas
 * celle-ci : le temps ajouté est alors passé à bouger. Mesuré sur le Pixel 7a, `am start -W`
 * donne 555 ms à froid, et ce temps-là est décompté — le surcoût est la différence, pas le
 * chiffre entier.
 */
const MIN_MS = 1950;
/** Sous ce seuil, on retire le calque SANS fondu : un écran qui apparaît et disparaît en
 *  quelques dizaines de millisecondes se lit comme un raté d'affichage, pas comme une marque. */
const CUT_MS = 120;
/** Doit rester égal à la transition de .splash dans style.css — voir la note là-bas. */
const FADE_MS = 420;

// Le rythme, et LE réglage de la vitesse : ces valeurs s'étirent ou se resserrent ensemble.
// Chaque étape arrive quand la précédente a fini de bouger — la silhouette, puis le doigt, puis
// la couleur, puis le nom, puis l'accroche. Seul l'anneau accompagne la vague : c'est le même
// mouvement, il en dessine le front.
//
// La vague met FLOOD_MS à atteindre la case la plus éloignée du museau, plus CELL_MS pour que
// celle-ci finisse de se poser : 650 ms, assez lent pour qu'on voie la couleur PARTIR du doigt
// au lieu de tout recouvrir d'un coup. Le nom part quand les dernières cases se posent.
const PAPER_MS = 200; // la silhouette blanche apparaît
const TAP_AT = 240; // le doigt touche…
const PRESS_MS = 160; // …et s'enfonce
const FLOOD_AT = 400; // la couleur part du museau
const FLOOD_MS = 480; // jusqu'à la case la plus lointaine
const CELL_MS = 170; // durée d'apparition d'une case
const RING_MS = 560; // l'anneau qui court devant la vague
const NAME_AT = 960;
const LINE_AT = 1140;
const IN_MS = 320;
/** Quand la dernière case est posée : l'image se redessine alors d'un seul tenant. */
const FLOOD_END = FLOOD_AT + FLOOD_MS + CELL_MS;

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
const { outCubic, outBack } = EASE;

/** Une case du renard : sa place, si elle entre dans la silhouette blanche, et son retard dans
 *  la vague (0 au museau, 1 à la case la plus lointaine). */
interface Cell {
  r: number;
  c: number;
  paper: boolean;
  wave: number;
}

let el: HTMLElement | null = null;
let cv: HTMLCanvasElement | null = null;
let g: CanvasRenderingContext2D | null = null;
let fox: HTMLImageElement | null = null;
let cells: Cell[] = [];
/** Le début de la séquence. Reste à 0 tant que l'image n'est pas là (ou abandonnée). */
let t0 = 0;
let raf = 0;
let closing = false;

/**
 * Les cases que le renard couvre, lues dans l'alpha de l'image. Réduite à 4 × G de côté et non
 * à G : un drawImage qui divise par 36 d'un coup échantillonne au lieu de moyenner, et une
 * oreille fine tomberait entre deux échantillons ; à 4 × G, la moyenne de chaque bloc 4 × 4
 * est faite ici. Un seul getImageData de 56 × 56, une fois — rien à voir avec la boucle de
 * rendu.
 */
function measure(img: HTMLImageElement): Cell[] {
  const k = 4;
  const n = G * k;
  const m = document.createElement('canvas');
  m.width = m.height = n;
  const x = m.getContext('2d', { willReadFrequently: true });
  if (!x) return [];
  x.drawImage(img, 0, 0, n, n);
  const a = x.getImageData(0, 0, n, n).data;
  const out: Cell[] = [];
  let far = 0;
  for (let r = 0; r < G; r++) {
    for (let c = 0; c < G; c++) {
      let sum = 0;
      for (let y = r * k; y < (r + 1) * k; y++) {
        for (let xx = c * k; xx < (c + 1) * k; xx++) sum += a[(y * n + xx) * 4 + 3];
      }
      const cover = sum / (k * k * 255);
      if (cover < 0.02) continue;
      const d = Math.hypot((c + 0.5) / G - TAP_X, (r + 0.5) / G - TAP_Y);
      far = Math.max(far, d);
      out.push({ r, c, paper: cover >= PAPER_COVER, wave: d });
    }
  }
  for (const cell of out) cell.wave /= far || 1;
  return out;
}

/** Le plus grand corps auquel `lines` tient dans `maxW`. Ni un chiffre choisi à la main ni un
 *  ratio : l'accroche fait 34 caractères et l'écran est étroit, donc la taille se déduit. */
function fit(font: (s: number) => string, lines: string[], maxW: number, ceiling: number): number {
  let lo = 6;
  let hi = ceiling;
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) / 2;
    g!.font = font(mid);
    const w = Math.max(...lines.map((l) => g!.measureText(l).width));
    if (w <= maxW) lo = mid;
    else hi = mid;
  }
  return lo;
}

const fName = (s: number): string => `700 ${s.toFixed(2)}px Fredoka, Nunito, sans-serif`;
const fLine = (s: number): string => `600 ${s.toFixed(2)}px Nunito, sans-serif`;

/** Le renard, posé selon `t` dans le carré (x, y, side). */
function figure(x: number, y: number, side: number, t: number): void {
  if (!fox) return;
  const c = g!;
  // La vague finie, l'image d'un seul tenant : découpée, elle garderait entre ses cases les
  // filets d'anticrénelage de chaque drawImage, une grille fantôme sur l'image arrêtée.
  if (t >= FLOOD_END) {
    c.drawImage(fox, x, y, side, side);
    return;
  }
  const s = side / G;
  const src = fox.naturalWidth / G;
  const appear = outCubic(clamp01(t / PAPER_MS));
  for (const cell of cells) {
    const p = clamp01((t - FLOOD_AT - cell.wave * FLOOD_MS) / CELL_MS);
    const cx = x + cell.c * s;
    const cy = y + cell.r * s;
    // La case blanche, tant que la couleur ne l'a pas entièrement recouverte : du papier et sa
    // réglure, rien dedans (cf. la note en tête de fichier).
    if (cell.paper && p < 1 && appear > 0) {
      const k = s * (0.86 + 0.14 * appear);
      const o = (s - k) / 2;
      c.globalAlpha = appear;
      c.fillStyle = PAPER;
      c.fillRect(cx + o, cy + o, k, k);
      c.strokeStyle = RULE;
      c.lineWidth = 1;
      c.strokeRect(cx + o + 0.5, cy + o + 0.5, k - 1, k - 1);
    }
    if (p > 0) {
      // Elle grandit en arrivant, avec le léger dépassement des cases qu'on remplit dans le jeu.
      const k = s * (0.86 + 0.14 * outBack(p));
      const o = (s - k) / 2;
      c.globalAlpha = p;
      c.drawImage(fox, cell.c * src, cell.r * src, src, src, cx + o, cy + o, k + 0.5, k + 0.5);
    }
  }
  c.globalAlpha = 1;
  tap(x + TAP_X * side, y + TAP_Y * side, s, side, t);
}

/** Le doigt : un disque blanc qui s'enfonce, puis l'anneau qui court devant la couleur. */
function tap(x: number, y: number, s: number, side: number, t: number): void {
  const c = g!;
  const press = clamp01((t - TAP_AT) / PRESS_MS);
  const ring = clamp01((t - FLOOD_AT) / RING_MS);
  if (press <= 0 || ring >= 1) return;
  c.save();
  c.fillStyle = `rgba(255, 255, 255, ${0.55 * (1 - ring)})`;
  c.beginPath();
  c.arc(x, y, s * (0.9 - 0.3 * outCubic(press)), 0, Math.PI * 2);
  c.fill();
  if (ring > 0) {
    c.strokeStyle = `rgba(255, 255, 255, ${0.8 * (1 - ring)})`;
    c.lineWidth = Math.max(2, side * 0.012);
    c.beginPath();
    c.arc(x, y, outCubic(ring) * side * 0.75, 0, Math.PI * 2);
    c.stroke();
  }
  c.restore();
}

/** Le nom, sous le dégradé de .home-head h1 — acc2 en haut, acc en bas du mot. */
function name(cx: number, base: number, size: number, a: number): void {
  if (a <= 0) return;
  const c = g!;
  c.save();
  c.globalAlpha = a;
  c.textAlign = 'center';
  c.font = fName(size);
  const grad = c.createLinearGradient(0, base - size * 0.78, 0, base + size * 0.08);
  grad.addColorStop(0, ACC2);
  grad.addColorStop(1, ACC_TXT);
  c.fillStyle = grad;
  c.fillText(NAME, cx, base - (1 - outCubic(a)) * size * 0.22);
  c.restore();
}

/** L'accroche, dans l'ENCRE DU THÈME et non plus en crème. Le crème tenait tant que le sol
 *  était mûre : une phrase couleur papier posée sur du sombre, c'était le même geste que le
 *  jeu — écrire sur la feuille. Sur le sol lavande du thème clair, le même crème tombe à
 *  1,3:1 et la phrase disparaît ; c'est ce qu'un joueur a remonté.
 *
 *  --ink et non l'accent, et l'intention d'origine est intacte : l'accent appartient au NOM
 *  sur cet écran, deux accents se disputeraient le regard. L'encre est neutre par définition,
 *  et elle est le seul jeton dont le rôle est « du texte sur le sol du thème » — donc elle est
 *  juste dans les trois thèmes sans condition à écrire ici (10:1 en clair, 8:1 en ciel, 14:1
 *  en soir). */
function line(cx: number, base: number, size: number, a: number): void {
  if (a <= 0) return;
  const c = g!;
  c.save();
  c.globalAlpha = a;
  c.textAlign = 'center';
  c.font = fLine(size);
  c.fillStyle = INK_UI;
  const lift = (1 - outCubic(a)) * size * 0.3;
  c.fillText(L1, cx, base - lift);
  c.fillText(L2, cx, base + size * 1.08 - lift);
  c.restore();
}

/** `t` négatif : la séquence n'a pas commencé (l'image est attendue), le sol seul. */
function draw(t: number): void {
  const c = g!;
  const W = cv!.clientWidth;
  const H = cv!.clientHeight;
  c.fillStyle = GROUND;
  c.fillRect(0, 0, W, H);
  if (t < 0) return;
  c.textBaseline = 'alphabetic';

  // L'unité de la composition, et il ne faut PAS que ce soit la largeur. Tout ce qui suit se
  // mesure en U — le renard, les deux corps de texte, les écarts — et sur un téléphone la
  // largeur est la petite dimension, donc s'en servir marchait. Sur un grand écran de bureau
  // elle est la GRANDE, et une composition mise à l'échelle de 2560 px déborde par le bas.
  //
  // H × 0,55 est le facteur qui laisse le portrait intact : sur un téléphone 411 × 880 il vaut
  // 484 et le minimum reste la largeur. Sur 2560 × 1400 il vaut 770, et la composition garde
  // ses proportions de portrait, centrée dans le grand écran au lieu de le remplir.
  const U = Math.min(W, H * 0.55);

  // La pile : le renard, le nom dessous, l'accroche dessous. Plus le verrou côte à côte de
  // l'en-tête, qu'on reprenait avec la diagonale : à côté du nom, le renard aurait la hauteur
  // du mot, et c'est lui qu'on est venu voir. Sur un 411 × 880, le groupe va de 181 à 607 px.
  const side = U * 0.66;
  const x = (W - side) / 2;
  const y = H * 0.36 - side / 2;
  figure(x, y, side, t);

  const nSize = fit(fName, [NAME], U * 0.6, U * 0.155);
  const nBase = y + side + U * 0.17;
  name(W / 2, nBase, nSize, clamp01((t - NAME_AT) / IN_MS));
  line(
    W / 2,
    nBase + U * 0.15,
    fit(fLine, [L1, L2], U * 0.84, U * 0.105),
    clamp01((t - LINE_AT) / IN_MS),
  );
}

function resize(): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  cv!.width = Math.round(cv!.clientWidth * dpr);
  cv!.height = Math.round(cv!.clientHeight * dpr);
  g!.setTransform(dpr, 0, 0, dpr, 0, 0);
}

/** Le temps de la séquence, ou -1 tant qu'elle n'a pas commencé. */
const clock = (now: number): number => (t0 ? now - t0 : -1);

/**
 * À appeler en tout premier, avant que quoi que ce soit d'autre ne dessine. Le calque est déjà
 * opaque dans le balisage : cet appel ne le montre pas, il l'anime.
 */
export function startSplash(): void {
  el = document.getElementById('splash');
  if (!el) return;
  cv = el.querySelector('canvas');
  if (!cv) return;
  g = cv.getContext('2d');
  if (!g) return;
  resize();
  window.addEventListener('resize', () => {
    if (!closing && cv) {
      resize();
      draw(noMotion() ? 9e9 : clock(performance.now()));
    }
  });

  // La séquence part quand le renard est là — ou sans lui, passé IMG_WAIT_MS.
  const go = (): void => {
    if (t0 || closing) return;
    t0 = performance.now();
    // Pas d'animation : l'état final, tout de suite. C'est ce que le calque propose, et le
    // faire apparaître par morceaux est précisément ce qu'on nous demande d'éviter.
    if (noMotion()) draw(9e9);
  };
  const img = new Image();
  img.onload = () => {
    if (t0) return;
    fox = img;
    cells = measure(img);
    go();
  };
  img.src = FOX_SRC;
  window.setTimeout(go, IMG_WAIT_MS);

  draw(-1);
  if (noMotion()) return;
  const loop = (now: number): void => {
    draw(clock(now));
    if (!closing) raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
}

/**
 * À appeler quand le catalogue est peint. Rend la main tout de suite si le minimum n'est pas
 * écoulé — le retrait s'arme alors pour plus tard, et l'appelant n'a rien à attendre.
 */
export function hideSplash(): void {
  if (!el || closing) return;
  // La séquence n'a pas commencé : l'image est attendue, IMG_WAIT_MS au plus.
  if (!t0) {
    window.setTimeout(hideSplash, 50);
    return;
  }
  const shown = performance.now() - t0;
  if (shown < MIN_MS) {
    window.setTimeout(hideSplash, MIN_MS - shown);
    return;
  }
  closing = true;
  cancelAnimationFrame(raf);
  const gone = (): void => {
    el?.remove();
    el = cv = null;
    g = null;
  };
  if (shown < CUT_MS || noMotion()) {
    gone();
    return;
  }
  el.classList.add('gone');
  // La transition CSS mène le fondu ; ce délai ne fait que retirer l'élément après elle. Un
  // `transitionend` serait plus juste et moins sûr : s'il ne vient pas, le calque reste et
  // couvre le jeu pour toujours.
  window.setTimeout(gone, FADE_MS + 40);
}
